"use client";

import { c as _c } from "react/compiler-runtime";
import { useId } from "react";
import { MessageSquare, Send, Star } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fa";
import "dayjs/locale/en";
import { useAuthStore } from "@/store/useAuthStore";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Avatar, Loading } from "@/features/home-v2/ui";
import type { CommentableModel } from "../types";
import { useCommentThread } from "../hooks/useCommentThread";
import "./journal-comments.css";
dayjs.extend(relativeTime);
type Props = {
  modelId: number;
  modelType: CommentableModel;
  lang: "fa" | "en";
};

/** The prompt names what is being discussed — this component serves articles,
 *  artists, collections, works and auctions, and "this article" on an artist
 *  or a collection was simply wrong. */
const PROMPT: Record<CommentableModel, {
  fa: string;
  en: string;
}> = {
  blog: {
    fa: "نظرتان را دربارهٔ این مقاله بنویسید...",
    en: "Share your thoughts on this article..."
  },
  collection: {
    fa: "نظرتان را دربارهٔ این کالکشن بنویسید...",
    en: "Share your thoughts on this collection..."
  },
  artist: {
    fa: "نظرتان را دربارهٔ این هنرمند بنویسید...",
    en: "Share your thoughts on this artist..."
  },
  nft: {
    fa: "نظرتان را دربارهٔ این اثر بنویسید...",
    en: "Share your thoughts on this work..."
  },
  auction: {
    fa: "نظرتان را دربارهٔ این حراج بنویسید...",
    en: "Share your thoughts on this auction..."
  }
};

/** The v2 comment section. Same thread hook as `CommentSection`; only the
 *  surface differs, so the two can never post different payloads. */
export default function JournalComments(t0) {
  const $ = _c(72);
  const {
    modelId,
    modelType,
    lang
  } = t0;
  const isFa = lang === "fa";
  const fieldId = useId();
  const openLoginModal = useAuthStore(_temp);
  let t1;
  if ($[0] !== isFa || $[1] !== modelId || $[2] !== modelType) {
    t1 = {
      modelId,
      modelType,
      isFa
    };
    $[0] = isFa;
    $[1] = modelId;
    $[2] = modelType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    user,
    comments,
    isLoading,
    isSubmitting,
    commentText,
    setCommentText,
    rating,
    setRating,
    submit
  } = useCommentThread(t1);
  const guestName = isFa ? "\u0645\u0647\u0645\u0627\u0646" : "Guest";
  let t2;
  if ($[4] !== openLoginModal || $[5] !== submit || $[6] !== user) {
    t2 = () => {
      if (!user) {
        openLoginModal();
        return;
      }
      submit();
    };
    $[4] = openLoginModal;
    $[5] = submit;
    $[6] = user;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onSubmit = t2;
  const t3 = isFa ? "rtl" : "ltr";
  const t4 = `${fieldId}-title`;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MessageSquare aria-hidden={true} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `${fieldId}-title`;
  const t7 = isFa ? "\u062F\u06CC\u062F\u06AF\u0627\u0647\u200C\u0647\u0627" : "Comments";
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <h3 id={t6}>{t7}</h3>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== comments.length) {
    t9 = comments.length > 0 ? <span className="v2-comments-count">{comments.length}</span> : null;
    $[12] = comments.length;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <header className="v2-comments-head">{t5}{t8}{t9}</header>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== comments || $[18] !== isFa || $[19] !== isLoading) {
    t11 = isLoading ? <Loading label={isFa ? "\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC" : "Loading"} /> : comments.length > 0 ? <ol className="v2-comments-list">{comments.map(comment => <li key={comment.id} className="v2-comments-item"><Avatar name={comment.name || "U"} src={comment.user?.avatar} size={36} /><div className="v2-comments-body"><div className="v2-comments-meta"><strong>{comment.name}</strong>{comment.rate ? <span className="v2-comments-stars" role="img" aria-label={isFa ? `${comment.rate} از ۵` : `${comment.rate} of 5`}>{Array.from({
                length: 5
              }, (_, i) => <Star key={i} aria-hidden={true} data-on={i < comment.rate || undefined} />)}</span> : null}{comment.created_at ? <time dateTime={comment.created_at}>{dayjs(comment.created_at).locale(isFa ? "fa" : "en").fromNow()}</time> : null}</div><p>{comment.content}</p></div></li>)}</ol> : <div className="v2-comments-empty"><MessageSquare aria-hidden={true} />{isFa ? "\u0627\u0648\u0644\u06CC\u0646 \u0646\u0641\u0631\u06CC \u0628\u0627\u0634\u06CC\u062F \u06A9\u0647 \u0646\u0638\u0631 \u0645\u06CC\u200C\u062F\u0647\u062F" : "Be the first to comment"}</div>;
    $[17] = comments;
    $[18] = isFa;
    $[19] = isLoading;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = isFa ? "\u062F\u06CC\u062F\u06AF\u0627\u0647 \u0634\u0645\u0627" : "Your comment";
  let t13;
  if ($[21] !== fieldId || $[22] !== t12) {
    t13 = <label className="v2-sr-only" htmlFor={fieldId}>{t12}</label>;
    $[21] = fieldId;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = PROMPT[modelType]?.[isFa ? "fa" : "en"];
  let t15;
  if ($[24] !== setCommentText) {
    t15 = e => setCommentText(e.target.value);
    $[24] = setCommentText;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== commentText || $[27] !== fieldId || $[28] !== isSubmitting || $[29] !== t14 || $[30] !== t15) {
    t16 = <textarea id={fieldId} className="v2-comments-textarea" rows={3} placeholder={t14} value={commentText} onChange={t15} disabled={isSubmitting} />;
    $[26] = commentText;
    $[27] = fieldId;
    $[28] = isSubmitting;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = user?.name || guestName;
  const t18 = user?.profile?.cover;
  let t19;
  if ($[32] !== t17 || $[33] !== t18) {
    t19 = <Avatar name={t17} src={t18} size={28} />;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const t20 = isFa ? "\u0627\u0645\u062A\u06CC\u0627\u0632 \u0634\u0645\u0627" : "Your rating";
  const t21 = isFa ? "\u0627\u0645\u062A\u06CC\u0627\u0632 \u0634\u0645\u0627" : "Your rating";
  let t22;
  if ($[35] !== t21) {
    t22 = <span>{t21}</span>;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = [1, 2, 3, 4, 5];
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== isFa || $[39] !== isSubmitting || $[40] !== rating || $[41] !== setRating) {
    t24 = <div>{t23.map(star => <button key={star} type="button" className="v2-focus" aria-label={isFa ? `${star} ستاره` : `${star} stars`} aria-pressed={rating === star} data-on={(rating ?? 0) >= star || undefined} onClick={() => setRating(star)} disabled={isSubmitting}><Star aria-hidden={true} /></button>)}</div>;
    $[38] = isFa;
    $[39] = isSubmitting;
    $[40] = rating;
    $[41] = setRating;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t20 || $[44] !== t22 || $[45] !== t24) {
    t25 = <div className="v2-comments-rating" role="group" aria-label={t20}>{t22}{t24}</div>;
    $[43] = t20;
    $[44] = t22;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== onSubmit) {
    t26 = () => void onSubmit();
    $[47] = onSubmit;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== commentText) {
    t27 = commentText.trim();
    $[49] = commentText;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  const t28 = !t27;
  let t29;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Send aria-hidden={true} />;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const t30 = isFa ? "\u062F\u0631 \u062D\u0627\u0644 \u0627\u0631\u0633\u0627\u0644\u2026" : "Posting\u2026";
  const t31 = isFa ? "\u0627\u0631\u0633\u0627\u0644" : "Post";
  let t32;
  if ($[52] !== isSubmitting || $[53] !== t26 || $[54] !== t28 || $[55] !== t30 || $[56] !== t31) {
    t32 = <ActionButton className="v2-btn--sm v2-comments-send" onClick={t26} disabled={t28} busy={isSubmitting} icon={t29} busyLabel={t30}>{t31}</ActionButton>;
    $[52] = isSubmitting;
    $[53] = t26;
    $[54] = t28;
    $[55] = t30;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t19 || $[59] !== t25 || $[60] !== t32) {
    t33 = <div className="v2-comments-bar">{t19}{t25}{t32}</div>;
    $[58] = t19;
    $[59] = t25;
    $[60] = t32;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== t13 || $[63] !== t16 || $[64] !== t33) {
    t34 = <div className="v2-comments-form"><div className="v2-comments-box">{t13}{t16}{t33}</div></div>;
    $[62] = t13;
    $[63] = t16;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== t10 || $[67] !== t11 || $[68] !== t3 || $[69] !== t34 || $[70] !== t4) {
    t35 = <section className="v2-comments" dir={t3} aria-labelledby={t4}>{t10}{t11}{t34}</section>;
    $[66] = t10;
    $[67] = t11;
    $[68] = t3;
    $[69] = t34;
    $[70] = t4;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  return t35;
}
function _temp(state) {
  return state.openLoginModal;
}